import { LandingNavbar } from "./LandingNavbar";
import { SearchSummaryBar } from "./SearchSummaryBar";
import { FilterSidebar } from "./FilterSidebar";
import { DepartureCard } from "./DepartureCard";
import { ArrowLeft, Filter, Users, Star } from "lucide-react";
import { useState } from "react";
import { BottomNavigation } from "./BottomNavigation";
import { AppHeader } from "./AppHeader";
import { useAuth } from '../hooks/useAuth';
import { useBooking } from '../hooks/useBooking';
import { useNavigation } from '../hooks/useNavigation';

/**
 * SearchResults - Resultados de búsqueda refactorizado
 * 
 * REFACTORIZADO:
 * - Ya no recibe props (antes recibía 10+ callbacks)
 * - Usa hooks para acceder a estado y acciones
 */
export const SearchResults = () => {
  // Hooks para acceder a servicios
  const { isLoggedIn, logout } = useAuth();
  const { searchParams, selectSeats } = useBooking();
  const { goToHome, goToProfile, goToMyTickets, goToSettings, goToLogin } = useNavigation();

  // 🛡️ Defensive Guards
  // const safeUser = user ?? { name: 'Usuario', email: '' };
  const safeSearchParams = searchParams ?? {
    origen: 'Ciudad de origen',
    destino: 'Ciudad de destino',
    fecha: new Date().toISOString().split('T')[0],
    pasajeros: '1',
  };

  const [showFilters, setShowFilters] = useState(false);
  const [expandedCardIndex, setExpandedCardIndex] = useState<
    number | null
  >(null);

  const handleCardExpand = (
    index: number,
    isExpanded: boolean,
  ) => {
    setExpandedCardIndex(isExpanded ? index : null);
  };

  // Mock data for departure cards
  const departures = [
    {
      company: "Estrella Blanca",
      rating: 5,
      ratingCount: 342,
      price: 450,
      departureTime: "08:30",
      departureLocation: "Terminal Central CDMX",
      arrivalTime: "15:00",
      arrivalLocation: "Terminal Guadalajara",
      duration: "6 h 30 min",
      amenities: [
        "A/C",
        "Wi-Fi",
        "Baño",
        "USB",
        "Asientos reclinables",
      ],
      availableSeats: 23,
      discount: 15,
      vehicleType: "Bus",
    },
    {
      company: "Primera Plus",
      rating: 4,
      ratingCount: 189,
      price: 520,
      departureTime: "10:00",
      departureLocation: "Terminal Central CDMX",
      arrivalTime: "16:45",
      arrivalLocation: "Terminal Guadalajara",
      duration: "6 h 45 min",
      amenities: ["A/C", "Wi-Fi", "Baño", "USB"],
    },
    {
      company: "ETN",
      rating: 5,
      ratingCount: 567,
      price: 580,
      departureTime: "12:30",
      departureLocation: "Terminal Central CDMX",
      arrivalTime: "19:15",
      arrivalLocation: "Terminal Guadalajara",
      duration: "6 h 45 min",
      amenities: [
        "A/C",
        "Wi-Fi",
        "Baño",
        "USB",
        "Asientos reclinables",
      ],
      availableSeats: 8,
      vehicleType: "Bus",
    },
    {
      company: "ADO",
      rating: 4,
      ratingCount: 421,
      price: 490,
      departureTime: "14:00",
      departureLocation: "Terminal Central CDMX",
      arrivalTime: "20:30",
      arrivalLocation: "Terminal Guadalajara",
      duration: "6 h 30 min",
      amenities: ["A/C", "Wi-Fi", "Baño"],
      availableSeats: 18,
      vehicleType: "Bus",
    },
    {
      company: "Omnibus",
      rating: 4,
      ratingCount: 234,
      price: 410,
      departureTime: "16:00",
      departureLocation: "Terminal Central CDMX",
      arrivalTime: "22:45",
      arrivalLocation: "Terminal Guadalajara",
      duration: "6 h 45 min",
      amenities: ["A/C", "Baño", "USB"],
      availableSeats: 4,
      discount: 10,
      vehicleType: "Sprinter",
    },
    {
      company: "Futura",
      rating: 5,
      ratingCount: 298,
      price: 555,
      departureTime: "18:30",
      departureLocation: "Terminal Central CDMX",
      arrivalTime: "01:00",
      arrivalLocation: "Terminal Guadalajara",
      duration: "6 h 30 min",
      amenities: [
        "A/C",
        "Wi-Fi",
        "Baño",
        "USB",
        "Asientos reclinables",
      ],
      availableSeats: 15,
      vehicleType: "Bus",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F7F8FA] pb-20 lg:pb-0">
      {/* App Header - Mobile Only */}
      <AppHeader
        className="lg:hidden"
        userName="Ana García"
        onProfileClick={goToProfile}
        onMyTicketsClick={goToMyTickets}
        onSettingsClick={goToSettings}
        onLogoutClick={logout}
        isLoggedIn={isLoggedIn}
        onLoginClick={goToLogin}
        onLogoClick={goToHome}
      />

      {/* Navbar - Hide on mobile */}
      <LandingNavbar
        className="hidden lg:block"
        onLogoClick={goToHome}
        isLoggedIn={isLoggedIn}
        userName="Ana García"
        onProfileClick={goToProfile}
        onMyTicketsClick={goToMyTickets}
        onSettingsClick={goToSettings}
        onLogoutClick={logout}
        onLoginClick={goToLogin}
      />

      {/* Mobile Header */}
      <div className="lg:hidden bg-[#D97540] text-white pt-3 pb-4 px-4">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={goToHome} className="p-1">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="font-semibold text-base">
              {safeSearchParams.origen || "Kanur"} →{" "}
              {safeSearchParams.destino || "Coimbatore"}
            </h1>
            <p className="text-xs opacity-90">
              {safeSearchParams.fecha || "20 Nov 2024"} |{" "}
              {safeSearchParams.pasajeros} pasajeros
            </p>
          </div>
        </div>

        {/* Filter Chips */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium rounded-[10px] hover:bg-white/20 transition-colors whitespace-nowrap"
          >
            <Filter className="w-4 h-4" />
            Filtrar
          </button>
          <button className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium rounded-[10px] hover:bg-white/20 transition-colors whitespace-nowrap">
            <Users className="w-4 h-4" />
            Tipo de asiento
          </button>
          <button className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium rounded-[10px] hover:bg-white/20 transition-colors whitespace-nowrap">
            <Star className="w-4 h-4" />
            Mejor calificados
          </button>
        </div>
      </div>

      {/* Desktop: Search Summary Bar */}
      <div className="hidden lg:block">
        <SearchSummaryBar
          searchParams={searchParams}
          onBackToHome={goToHome}
        />
      </div>

      {/* Main Content Area */}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-8 py-4 lg:py-8">
        {/* Desktop: Back Button */}
        <button
          onClick={goToHome}
          className="hidden lg:flex items-center gap-2 text-[#6B7280] hover:text-[#FF6A2A] font-medium mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Volver al inicio
        </button>

        <div className="grid grid-cols-12 gap-8">
          {/* Left Sidebar: Filters - Desktop Only */}
          <div className="hidden lg:block col-span-12 lg:col-span-3">
            <FilterSidebar />
          </div>

          {/* Mobile: Filters Modal */}
          {showFilters && (
            <div className="lg:hidden fixed inset-0 bg-black/50 z-50">
              <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[16px] p-4 max-h-[80vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-[#111827]">
                    Filtros
                  </h3>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="text-[#6B7280] hover:text-[#111827]"
                  >
                    Cerrar
                  </button>
                </div>
                <FilterSidebar />
              </div>
            </div>
          )}

          {/* Right Content: Results List */}
          <div className="col-span-12 lg:col-span-9">
            {/* Results Header - Desktop Only */}
            <div className="hidden lg:block mb-6">
              <h2 className="text-2xl font-bold text-[#111827] mb-2">
                Salidas disponibles
              </h2>
              <p className="text-[#6B7280]">
                Encontramos {departures.length} opciones para tu
                viaje
              </p>
            </div>

            {/* Departure Cards */}
            <div className="space-y-3 lg:space-y-4">
              {departures.map((departure, index) => (
<DepartureCard
  key={index}
  {...departure}
  availableSeats={departure.availableSeats ?? 0}
  onSelectSeats={selectSeats}
  onExpand={(_, isExpanded) => handleCardExpand(index, isExpanded)}
  isExpanded={expandedCardIndex === index}
/>

              ))}
            </div>

            {/* Load More (Optional) */}
            <div className="mt-8 text-center">
              <button className="px-8 py-3 bg-white text-[#111827] border border-[#E5E7EB] rounded-[14px] hover:bg-[#F7F8FA] transition-all font-medium">
                Cargar más resultados
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation - Mobile Only */}
      <div className="lg:hidden">
        <BottomNavigation 
          onAccountClick={goToProfile} 
          onMyTripsClick={goToMyTickets} 
          onHomeClick={goToHome}
        />
      </div>
    </div>
  );
};

export default SearchResults;